import config from '../utils/config.js';

class EsimProvider {
    /**
     * @param {Object} options - Provider options
     * @param {string} options.baseUrl - API base URL (optional)
     */
    constructor({ baseUrl } = {}) {
        this.baseUrl = baseUrl || config.api.esim;
        this._cache = {
            packages: new Map(),
            locations: null
        };
        this._cacheStorageKey = 'esim_provider_cache';
        this._pendingRequests = new Map();
        this._loadCacheFromStorage();
    }

    /**
     * Loads cache from localStorage
     */
    _loadCacheFromStorage() {
        try {
            const stored = localStorage.getItem(this._cacheStorageKey);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.packages) {
                    Object.entries(parsed.packages).forEach(([key, value]) => {
                        if (value && typeof value === 'object' && !value.timestamp) {
                            value = { data: value, timestamp: Date.now() };
                        }
                        this._cache.packages.set(key, value);
                    });
                }
                if (parsed.locations) {
                    if (parsed.locations && typeof parsed.locations === 'object' && !parsed.locations.timestamp) {
                        this._cache.locations = { data: parsed.locations, timestamp: Date.now() };
                    } else {
                        this._cache.locations = parsed.locations;
                    }
                }
            }
        } catch (error) {
            console.error('Error loading cache from storage:', error);
        }
    }

    /**
     * Saves cache to localStorage
     */
    _saveCacheToStorage() {
        try {
            const packagesObj = {};
            this._cache.packages.forEach((value, key) => {
                packagesObj[key] = value;
            });
            const toStore = {
                packages: packagesObj,
                locations: this._cache.locations
            };
            localStorage.setItem(this._cacheStorageKey, JSON.stringify(toStore));
        } catch (error) {
            console.error('Error saving cache to storage:', error);
        }
    }

    /**
     * Wraps data with timestamp for cache tracking
     * @param {*} data - Data to wrap
     * @returns {Object} Wrapped data with timestamp
     */
    _wrapCacheData(data) {
        return {
            data: data,
            timestamp: Date.now()
        };
    }

    /**
     * Extracts data from cache wrapper
     * @param {*} cached - Cached value (may be wrapped or raw)
     * @returns {*} Extracted data
     */
    _unwrapCacheData(cached) {
        if (cached && typeof cached === 'object' && 'data' in cached && 'timestamp' in cached) {
            return cached.data;
    }
        return cached;
    }

    /**
     * @returns {Object} Default request headers
     */
    get headers() {
        return {
            'Content-Type': 'application/json'
        };
    }

    /**
     * Generates a cache key from options object
     * @param {Object} options - Options object
     * @returns {string} Cache key
     */
    _getCacheKey(options) {
        const keys = Object.keys(options).sort();
        return keys.map(key => `${key}:${options[key]}`).join('|') || 'default';
    }

    /**
     * Clears the cache for packages and/or locations
     * @param {string} type - 'packages', 'locations', or 'all' (default)
     */
    clearCache(type = 'all') {
        if (type === 'all' || type === 'packages') {
            this._cache.packages.clear();
        }
        if (type === 'all' || type === 'locations') {
            this._cache.locations = null;
        }
        this._saveCacheToStorage();
    }

    /**
     * Gets available packages (BASE/TOPUP) and top-ups
     * @param {Object} options - Filter options
     * @param {string} options.locationCode - ISO-2 code or !RG (Regional) or !GL (Global)
     * @param {string} options.type - BASE or TOPUP
     * @param {string} options.packageCode - Package code filter
     * @param {string} options.slug - Package slug alias
     * @param {string} options.iccid - ICCID for TOPUP compatibility
     * @returns {Promise<Object>} API response
     */
    async packages(options = {}) {
        const cacheKey = this._getCacheKey(options);
        const url = `${this.baseUrl}/package/list`;
        const body = {};

        if (options.locationCode) body.locationCode = options.locationCode;
        if (options.type) body.type = options.type;
        if (options.packageCode) body.packageCode = options.packageCode;
        if (options.slug) body.slug = options.slug;
        if (options.iccid) body.iccid = options.iccid;

        if (this._cache.packages.has(cacheKey)) {
            const cachedValue = this._cache.packages.get(cacheKey);
            const cachedData = this._unwrapCacheData(cachedValue);
            
            if (!this._pendingRequests.has(cacheKey)) {
                const fetchPromise = fetch(url, {
                    method: 'POST',
                    headers: this.headers,
                    body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
                })
                    .then(res => {
                        if (!res.ok) {
                            throw new Error(`HTTP error! status: ${res.status}`);
                        }
                        return res.json();
                    })
                    .then(data => {
                        this._cache.packages.set(cacheKey, this._wrapCacheData(data));
                        this._saveCacheToStorage();
                        this._pendingRequests.delete(cacheKey);
                    })
                    .catch(error => {
                        console.error('Error updating packages cache:', error);
                        this._pendingRequests.delete(cacheKey);
                    });
                
                this._pendingRequests.set(cacheKey, fetchPromise);
            }
            
            return cachedData;
        }

        if (this._pendingRequests.has(cacheKey)) {
            await this._pendingRequests.get(cacheKey);
            const cachedValue = this._cache.packages.get(cacheKey);
            if (cachedValue) {
                return this._unwrapCacheData(cachedValue);
            }
        }

        const fetchPromise = fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
        })
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
        return res.json();
            });
        
        this._pendingRequests.set(cacheKey, fetchPromise);
        
        const data = await fetchPromise;
        
        this._cache.packages.set(cacheKey, this._wrapCacheData(data));
        this._saveCacheToStorage();
        this._pendingRequests.delete(cacheKey);
        
        return data;
    }

    /**
     * Gets all packages with favorite = true
     * @returns {Promise<Object>} API response with filtered package list containing only favorites
     */
    async favorites() {
        const allPackages = await this.packages({});
        
        if (!allPackages.success || !allPackages.obj?.packageList) {
            return allPackages;
        }

        const favoritePackages = allPackages.obj.packageList.filter(
            pkg => pkg.favorite === true
        );

        return {
            ...allPackages,
            obj: {
                ...allPackages.obj,
                packageList: favoritePackages
            }
        };
    }

    /**
     * Gets supported regions/countries and codes
     * @returns {Promise<Object>} API response with location list
     */
    async locations() {
        const url = `${this.baseUrl}/location/list`;
        const cacheKey = 'locations';

        if (this._cache.locations !== null) {
            const cachedData = this._unwrapCacheData(this._cache.locations);
            
            if (!this._pendingRequests.has(cacheKey)) {
                const fetchPromise = fetch(url, {
                    method: 'POST',
                    headers: this.headers,
                })
                    .then(res => {
                        if (!res.ok) {
                            throw new Error(`HTTP error! status: ${res.status}`);
                        }
                        return res.json();
                    })
                    .then(data => {
                        this._cache.locations = this._wrapCacheData(data);
                        this._saveCacheToStorage();
                        this._pendingRequests.delete(cacheKey);
                    })
                    .catch(error => {
                        console.error('Error updating locations cache:', error);
                        this._pendingRequests.delete(cacheKey);
                    });
                
                this._pendingRequests.set(cacheKey, fetchPromise);
            }
            
            return cachedData;
        }

        if (this._pendingRequests.has(cacheKey)) {
            await this._pendingRequests.get(cacheKey);
            if (this._cache.locations !== null) {
                return this._unwrapCacheData(this._cache.locations);
            }
        }

        const fetchPromise = fetch(url, {
            method: 'POST',
            headers: this.headers,
        })
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
        return res.json();
            });
        
        this._pendingRequests.set(cacheKey, fetchPromise);
        
        const data = await fetchPromise;
        
        this._cache.locations = this._wrapCacheData(data);
        this._saveCacheToStorage();
        this._pendingRequests.delete(cacheKey);
        
        return data;
    }

    /**
     * Orders eSIM profiles (individual or batch)
     * @param {Object} orderData - Order data
     * @param {string} orderData.transactionId - Unique transaction ID (max 50 chars)
     * @param {Array} orderData.packageInfoList - List of packages to order
     * @param {number} orderData.amount - Total amount (optional, in units *10000)
     * @returns {Promise<Object>} API response
     */
    async order(orderData) {
        const url = `${this.baseUrl}/esim/order`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(orderData),
        });
        return res.json();
    }

    /**
     * Queries eSIMs by orderNo, ICCID, or date range
     * @param {Object} queryData - Query parameters
     * @param {string} queryData.orderNo - Order number
     * @param {string} queryData.iccid - Specific ICCID
     * @param {string} queryData.startTime - Start date (ISO format)
     * @param {string} queryData.endTime - End date (ISO format)
     * @param {Object} queryData.pager - Pagination (pageNum, pageSize)
     * @returns {Promise<Object>} API response
     */
    async query(queryData) {
        const url = `${this.baseUrl}/esim/query`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(queryData),
        });
        return res.json();
    }

    /**
     * Cancels eSIM profile (not installed) with refund
     * @param {Object} identifier - eSIM identifier
     * @param {string} identifier.iccid - ICCID
     * @param {string} identifier.esimTranNo - Transaction number
     * @returns {Promise<Object>} API response
     */
    async cancel(identifier) {
        const url = `${this.baseUrl}/esim/cancel`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(identifier),
        });
        return res.json();
    }

    /**
     * Suspends/pauses eSIM data
     * @param {Object} identifier - eSIM identifier (iccid or esimTranNo)
     * @returns {Promise<Object>} API response
     */
    async suspend(identifier) {
        const url = `${this.baseUrl}/esim/suspend`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(identifier),
        });
        return res.json();
    }

    /**
     * Reactivates suspended eSIM data
     * @param {Object} identifier - eSIM identifier (iccid or esimTranNo)
     * @returns {Promise<Object>} API response
     */
    async unsuspend(identifier) {
        const url = `${this.baseUrl}/esim/unsuspend`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(identifier),
        });
        return res.json();
    }

    /**
     * Revokes active eSIM profile without refund
     * @param {Object} identifier - eSIM identifier (iccid or esimTranNo)
     * @returns {Promise<Object>} API response
     */
    async revoke(identifier) {
        const url = `${this.baseUrl}/esim/revoke`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(identifier),
        });
        return res.json();
    }

    /**
     * Top-ups existing eSIM with compatible package
     * @param {Object} topupData - Top-up data
     * @param {string} topupData.esimTranNo - Transaction number (preferred)
     * @param {string} topupData.packageCode - Top-up package code
     * @param {string} topupData.transactionId - Unique transaction ID
     * @param {string} topupData.amount - Price *10000 (optional, validated if sent)
     * @returns {Promise<Object>} API response
     */
    async topup(topupData) {
        const url = `${this.baseUrl}/esim/topup`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(topupData),
        });
        return res.json();
    }

    /**
     * Sends SMS to eSIM (requires installed eSIM with SMS support)
     * @param {Object} smsData - SMS data
     * @param {string} smsData.esimTranNo - Transaction number
     * @param {string} smsData.message - Message text (max 500 chars)
     * @returns {Promise<Object>} API response
     */
    async sendsms(smsData) {
        const url = `${this.baseUrl}/esim/sendSms`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(smsData),
        });
        return res.json();
    }

    /**
     * Queries data usage for eSIMs (up to 10, updates every 2-3 hours)
     * @param {Array<string>} esimTranNoList - Array of transaction numbers (max 10)
     * @returns {Promise<Object>} API response
     */
    async usage(esimTranNoList) {
        const url = `${this.baseUrl}/esim/usage/query`;
        const res = await fetch(url, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify({ esimTranNoList }),
        });
        return res.json();
    }
}

export default EsimProvider;
